/**
 * Routage des événements de ressources partagées côté spectateur.
 *
 * L'horodatage empilé dans la file du player reste celui de la réception : la file ordonne les
 * actions par horodatage croissant, tous types de ressources confondus, et offres, quiz et
 * diapositives sont estampillés côté client. Y mêler le `displayedAt` serveur inverse l'ordre dès
 * que deux actions s'enchaînent plus vite que l'écart entre les horloges.
 */

/**
 * En evergreen le back émet déjà l'événement au bon offset de la timeline, et aucun player HLS
 * n'est monté : recaler reviendrait à ne jamais afficher la ressource.
 */
export const shouldSyncSharedResourceOnPlayer = (
  isEvergreen: boolean,
  hasPlayerHandle: boolean,
): boolean => !isEvergreen && hasPlayerHandle;

export type SharedResourceEventType = 'RESOURCE_SHOW' | 'RESOURCE_HIDE';

/**
 * L'evergreen rejoue les événements de la timeline sans les traduire : le type reçu est
 * `SHARED_RESOURCE`, et c'est `{"action":"HIDE"}` dans le payload qui distingue un masquage, là où
 * le live émet RESOURCE_SHOW / RESOURCE_HIDE. Même convention que les offres, quiz et sondages
 * evergreen (cf. ChatView). Retourne null pour un type inconnu, à ignorer par l'appelant.
 */
export const normalizeSharedResourceEventType = (
  eventType: string | undefined,
  payloadJson: string | undefined,
): SharedResourceEventType | null => {
  if (eventType === 'RESOURCE_SHOW' || eventType === 'RESOURCE_HIDE') {
    return eventType;
  }
  if (eventType !== 'SHARED_RESOURCE') {
    return null;
  }
  try {
    const payload = payloadJson ? JSON.parse(payloadJson) : null;
    return payload?.action === 'HIDE' ? 'RESOURCE_HIDE' : 'RESOURCE_SHOW';
  } catch {
    return 'RESOURCE_SHOW';
  }
};

export type SharedResourceEventAction = 'sync-on-player' | 'apply-now' | 'refetch-current';

/**
 * Un SHOW ne transporte l'état à afficher que dans son `payloadJson`. Quand celui-ci est
 * inexploitable, abandonner l'événement laisse le panneau vide alors que le back considère la
 * ressource diffusée, jusqu'au prochain retour de focus. On refetch plutôt que de jeter.
 */
export const resolveSharedResourceEventAction = ({
  eventType,
  hasResource,
  isEvergreen,
  hasPlayerHandle,
}: {
  eventType: SharedResourceEventType;
  hasResource: boolean;
  isEvergreen: boolean;
  hasPlayerHandle: boolean;
}): SharedResourceEventAction => {
  if (eventType === 'RESOURCE_SHOW' && !hasResource) {
    return 'refetch-current';
  }
  return shouldSyncSharedResourceOnPlayer(isEvergreen, hasPlayerHandle)
    ? 'sync-on-player'
    : 'apply-now';
};
